// View model for the Microsoft Secure Score column of By area on the Security page (pages/security/ByArea.jsx).
//
// The collector (lambda/security-checks.mjs, extractMicrosoftSecureScore)
// stores per category { score, controlCount, shareOfMax } and, when the
// control profiles cover every scored control of that category,
// { maxScore, percentage }: the category maximum is the sum of the profiles'
// maxScore for the controls Microsoft scored there. Runs collected before that
// change, or with incomplete profiles, have no maximum: those categories are
// points-only (no bar, no percentage) rather than a made-up proportion.

// Microsoft's own category order, then anything else alphabetically.
const ORDER = ['Identity', 'Apps', 'Data', 'Device', 'Infrastructure'];

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * Ordered category rows for display.
 * @returns {{ key: string, label: string, score: number, maxScore: number|null, percentage: number|null }[]}
 */
export function secureScoreCategories(msScore) {
  const cats = msScore?.categories ?? {};
  const keys = Object.keys(cats).filter((k) => cats[k] && finite(cats[k].score));
  keys.sort((a, b) => {
    const ia = ORDER.indexOf(a);
    const ib = ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? ORDER.length : ia) - (ib === -1 ? ORDER.length : ib);
    return a.localeCompare(b);
  });
  return keys.map((key) => {
    const c = cats[key];
    const hasMax = finite(c.maxScore) && c.maxScore > 0;
    const percentage = !hasMax ? null : (finite(c.percentage) ? c.percentage : (c.score / c.maxScore) * 100);
    return {
      key,
      label: key,
      score: c.score,
      maxScore: hasMax ? c.maxScore : null,
      percentage,
    };
  });
}

/** 711.74 -> "711.7", 1219 -> "1,219", 70 -> "70". */
export function formatPoints(n) {
  return finite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 1 }) : '--';
}

/** 98.59 -> "98.6%", 100 -> "100%". */
export function formatPercent(n) {
  return finite(n) ? `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}%` : '--';
}

/**
 * Microsoft calculates the score per UTC day (createdDateTime is midnight UTC),
 * so the date is shown in UTC: a local time zone would move it a day back.
 */
export function formatScoreDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * The Identity disclaimer, kept from the old Microsoft Secure Score card: By area on the Security page shows it under
 * the Microsoft column whenever the Identity category is present.
 */
export const IDENTITY_NOTE =
  'The Identity row is the Identity category of the Microsoft 365 Defender Secure Score (points earned of the points available for identity controls in this score). It is not the Identity Secure Score in the Microsoft Entra admin center, which is calculated separately from Entra recommendations and can differ.';
